import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, ToggleOn, ToggleOff, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { getDecks, createDeck, updateDeck, deleteDeck, getLanguage, getPackageNames } from '../services/api';

interface Deck {
  id: string;
  name: string;
  type: string;
  description: string;
  emoji: string;
  cardCount: number;
  background: {
    startColor: string;
    endColor: string;
  };
  packageId: string;
  status: 'active' | 'inactive';
  order?: number;
  createdAt: string;
  updatedAt: string;
}

interface DecksResponse {
  decks: Deck[];
}

const DeckSettings: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useError();
  const [decks, setDecks] = useState<Deck[]>([]);
  const [packages, setPackages] = useState<string[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDeck, setEditingDeck] = useState<Deck | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage] = useState<string>(getLanguage());
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    type: '',
    description: '',
    emoji: '',
    background: {
      startColor: '',
      endColor: '',
    },
    packageId: '',
    status: 'active' as 'active' | 'inactive',
    cardCount: 0,
    order: 0,
  });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState<Deck | null>(null);
  const [pendingStatus, setPendingStatus] = useState<'active' | 'inactive'>('active');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const fetchPackages = async () => {
    try {
      const response = await getPackageNames();
      if (response && response.data) {
        setPackages(response.data);
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
      showError('Failed to fetch packages. Please try again.');
    }
  };

  const fetchDecks = async () => {
    try {
      setLoading(true);
      const response = await getDecks();
      console.log('API Response:', response);
      
      if (response && response.data) {
        const decksResponse = response.data as DecksResponse;
        const decksData = Array.isArray(decksResponse.decks) ? decksResponse.decks : [];
        console.log('Decks data:', decksData);
        const sortedDecks = [...decksData].sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        setDecks(sortedDecks);
      } else {
        console.warn('No data received from the server');
        setDecks([]);
      }
    } catch (err) {
      console.error('Error fetching decks:', err);
      showError('Failed to fetch decks. Please try again.');
      setDecks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('DeckSettings component mounted');
    fetchDecks();
    fetchPackages();
  }, [selectedLanguage]);

  const handleOpenDialog = (deck?: Deck) => {
    if (deck) {
      setEditingDeck(deck);
      setFormData({
        id: deck.id,
        name: deck.name,
        type: deck.type,
        description: deck.description,
        emoji: deck.emoji,
        background: deck.background,
        packageId: deck.packageId,
        status: deck.status?.toLowerCase() === 'inactive' ? 'active' : (deck.status?.toLowerCase() === 'active' ? 'active' : deck.status),
        cardCount: deck.cardCount,
        order: deck.order ?? 0,
      });
    } else {
      setEditingDeck(null);
      setFormData({
        id: '',
        name: '',
        type: '',
        description: '',
        emoji: '',
        background: {
          startColor: '',
          endColor: '',
        },
        packageId: '',
        status: 'active' as 'active' | 'inactive',
        cardCount: 0,
        order: decks.length,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDeck(null);
    setFormData({
      id: '',
      name: '',
      type: '',
      description: '',
      emoji: '',
      background: {
        startColor: '',
        endColor: '',
      },
      packageId: '',
      status: 'active' as 'active' | 'inactive',
      cardCount: 0,
      order: 0,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingDeck) {
        const response = await updateDeck(editingDeck.id, formData);
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to update deck');
        }
      } else {
        if (!formData.id) {
          showError('Deck ID is required');
          return;
        }
        if (!formData.packageId) {
          showError('Package ID is required');
          return;
        }
        await createDeck(formData);
      }
      handleCloseDialog();
      fetchDecks();
    } catch (err) {
      console.error('Error saving deck:', err);
      showError(err instanceof Error ? err.message : 'Failed to save deck. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this deck?')) {
      try {
        await deleteDeck(id);
        fetchDecks();
      } catch (err) {
        console.error('Error deleting deck:', err);
        showError('Failed to delete deck. Please try again.');
      }
    }
  };

  const handleStatusToggle = (deck: Deck) => {
    setSelectedDeck(deck);
    setPendingStatus(deck.status?.toLowerCase() === 'inactive' ? 'active' : 'inactive');
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedDeck(null);
    setPendingStatus('active');
  };

  const confirmStatusChange = async () => {
    if (!selectedDeck) return;
    
    try {
      await updateDeck(selectedDeck.id, { ...selectedDeck, status: pendingStatus });
      fetchDecks();
    } catch (err) {
      console.error('Error toggling deck status:', err);
      showError('Failed to update deck status. Please try again.');
    } finally {
      handleCloseStatusDialog();
    }
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && index !== dragOverIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const persistOrder = async (orderedDecks: Deck[]) => {
    const changed = orderedDecks
      .map((deck, index) => ({ deck, index }))
      .filter(({ deck, index }) => deck.order !== index);

    if (changed.length === 0) return;

    try {
      setSavingOrder(true);
      await Promise.all(
        changed.map(({ deck, index }) => updateDeck(deck.id, { ...deck, order: index }))
      );
      setDecks((prev) => prev.map((deck, index) => ({ ...deck, order: index })));
    } catch (err) {
      console.error('Error saving deck order:', err);
      showError('Failed to save new deck order. Please try again.');
      fetchDecks();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    const sourceIndex = draggedIndex;
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (sourceIndex === null || sourceIndex === dropIndex) return;

    const reordered = [...decks];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setDecks(reordered);
    persistOrder(reordered);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Deck Settings
          </Typography>
        </Box>
        <Typography variant="h6" gutterBottom>
          Manage your game decks ({selectedLanguage.toUpperCase()})
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog()}
          >
            Add New Deck
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {savingOrder && <CircularProgress size={16} />}
            <Typography variant="body2" color="text.secondary">
              {savingOrder ? 'Saving order...' : 'Drag cards by the handle to reorder decks'}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : decks.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>No decks found. Create your first deck!</Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {decks.map((deck, index) => (
              <Grid item xs={12} sm={6} md={4} key={deck.id}>
                <Card
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: deck.background.startColor,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: draggedIndex === index ? 0.4 : 1,
                    outline: dragOverIndex === index && draggedIndex !== null && draggedIndex !== index
                      ? '3px dashed rgba(255, 255, 255, 0.9)'
                      : 'none',
                    outlineOffset: '-3px',
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  <Box
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.35)',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    <DragIndicatorIcon sx={{ color: 'white', fontSize: '1.25rem' }} />
                  </Box>
                  <Box
                    sx={{
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '3rem',
                      position: 'relative',
                      zIndex: 2,
                    }}
                  >
                    {deck.emoji}
                  </Box>
                  <CardContent sx={{ 
                    flexGrow: 1, 
                    position: 'relative', 
                    zIndex: 2,
                    background: 'rgba(0, 0, 0, 0.4)',
                    borderRadius: '4px',
                    margin: '8px',
                    padding: '16px'
                  }}>
                    <Typography 
                      variant="h6" 
                      component="div" 
                      gutterBottom 
                      sx={{ 
                        textAlign: 'center',
                        color: 'white',
                        fontSize: '1.75rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {deck.name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'white', fontSize: '1.1rem' }}>
                      Status: <span style={{ color: deck.status === 'active' ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>{deck.status.charAt(0).toUpperCase() + deck.status.slice(1)}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'white', fontSize: '1.1rem' }}>
                      ID: <span style={{ fontWeight: 'bold' }}>{deck.id}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'white', fontSize: '1.1rem' }}>
                      Type: <span style={{ fontWeight: 'bold' }}>{deck.type}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'white', fontSize: '1.1rem' }}>
                      Description: <span style={{ fontWeight: 'bold' }}>{deck.description}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'white', fontSize: '1.1rem' }}>
                      Cards: <span style={{ fontWeight: 'bold' }}>{deck.cardCount}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'white', fontSize: '1.1rem' }}>
                      Package: <span style={{ fontWeight: 'bold' }}>{deck.packageId}</span>
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'white', opacity: 0.9, fontSize: '0.7rem' }}>
                        Created: <span style={{ fontWeight: 'bold' }}>{new Date(deck.createdAt).toLocaleDateString()}</span>
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'white', opacity: 0.9, fontSize: '0.7rem' }}>
                        Updated: <span style={{ fontWeight: 'bold' }}>{new Date(deck.updatedAt).toLocaleDateString()}</span>
                      </Typography>
                    </Box>
                  </CardContent>
                  <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', position: 'relative', zIndex: 2 }} />
                  <CardActions sx={{ justifyContent: 'space-between', p: 2, position: 'relative', zIndex: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleStatusToggle(deck)}
                      sx={{ 
                        minWidth: '100px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      {deck.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                    <IconButton 
                      aria-label="edit" 
                      onClick={() => handleOpenDialog(deck)}
                      sx={{ color: 'white' }}
                    >
                      <EditIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editingDeck ? 'Edit Deck' : 'Add New Deck'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Deck ID"
                    fullWidth
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                    required
                    disabled={!!editingDeck}
                    helperText={editingDeck ? "Deck ID cannot be changed" : "Unique identifier for the deck"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Name"
                    fullWidth
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    inputProps={{ maxLength: 14 }}
                    helperText={`${formData.name.length}/14`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    inputProps={{ maxLength: 130 }}
                    helperText={`${formData.description.length}/130`}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>Package</InputLabel>
                    <Select
                      value={formData.packageId}
                      onChange={(e) => setFormData({ ...formData, packageId: e.target.value })}
                      label="Package"
                      required
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {packages.map((pkg) => (
                        <MenuItem key={pkg} value={pkg}>
                          {pkg}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="Type"
                    fullWidth
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="Card Count"
                    type="number"
                    fullWidth
                    value={formData.cardCount}
                    onChange={(e) => setFormData({ ...formData, cardCount: parseInt(e.target.value) || 0 })}
                    required
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="Emoji"
                    fullWidth
                    value={formData.emoji}
                    onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="BG Start"
                    fullWidth
                    value={formData.background.startColor}
                    onChange={(e) => setFormData({
                      ...formData,
                      background: { ...formData.background, startColor: e.target.value }
                    })}
                    required
                    InputProps={{
                      endAdornment: (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            backgroundColor: formData.background.startColor,
                            border: '1px solid #ccc',
                            ml: 1
                          }}
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="BG End"
                    fullWidth
                    value={formData.background.endColor}
                    onChange={(e) => setFormData({
                      ...formData,
                      background: { ...formData.background, endColor: e.target.value }
                    })}
                    InputProps={{
                      endAdornment: (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            backgroundColor: formData.background.endColor || 'transparent',
                            border: '1px solid #ccc',
                            ml: 1
                          }}
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                {editingDeck ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Paper>
      <Dialog
        open={statusDialogOpen}
        onClose={handleCloseStatusDialog}
      >
        <DialogTitle>Change Deck Status</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Update status for "{selectedDeck?.name}":
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={pendingStatus}
              label="Status"
              onChange={(e) => setPendingStatus(e.target.value as 'active' | 'inactive')}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancel</Button>
          <Button 
            onClick={confirmStatusChange} 
            variant="contained" 
            color={pendingStatus === 'active' ? 'success' : 'error'}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeckSettings; 